/**
 * pj-collection-subnav
 * --------------------
 * Horizontal card slider for the sub-collection nav in PJ_CollectionMain.
 *
 * Loaded as an asset rather than inlined because the theme's facet-form
 * replaces the WHOLE section on every filter/sort change
 * (facet-form.js: shopifySection.replaceChildren(...)). An inline script would
 * be re-sent inside every one of those ~170KB section payloads; an external
 * one is fetched once and served from cache thereafter.
 *
 * That same full-section replace is why scroll position is remembered in a
 * module-level Map: the element is destroyed and rebuilt on each filter
 * change, and a shopper who scrolled the strip should not be sent back to
 * the first card just because they ticked "In stock".
 */
(function () {
  if (window.__PJ_CM_SUBSLIDER__ && window.__PJ_CM_SUBSLIDER__.initialized) return;
  window.__PJ_CM_SUBSLIDER__ = { version: 'asset-v1', initialized: true };

  /**
   * Everything that can only work with JavaScript — the sidebar toggle, and
   * therefore the collapsed state itself — hangs off this class. Set here
   * rather than in init() so it lands as soon as the deferred script runs,
   * before DOMContentLoaded.
   */
  document.documentElement.classList.add('pj-js');

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Survives the section swap; keyed by section id so multiple instances
  // (or a section re-render) each get their own position back.
  var scrollMemory = new Map();

  class PJCollectionSubSlider extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('.pj-cm-sub__track');
      this.prevBtn = this.querySelector('[data-pj-sub-prev]');
      this.nextBtn = this.querySelector('[data-pj-sub-next]');
      this.navStyle = this.dataset.navStyle || 'floating';
      this.memoryKey = this.dataset.sectionId || 'pj-cm-sub';
      this.rafId = null;
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this._drag = { active: false, moved: false, startX: 0, startScroll: 0 };
    }

    connectedCallback() {
      if (!this.track) return;

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByOne(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByOne(1));

      this.setupDrag();
      this.restoreScroll();

      // The media box is aspect-ratio'd, so its height is known before the
      // image decodes -- but not necessarily before this element upgrades.
      // Re-measure once layout settles and whenever the box resizes.
      var media = this.querySelector('.pj-cm-sub__media');
      if (media && 'ResizeObserver' in window) {
        this.mediaObserver = new ResizeObserver(() => this.positionFloatingArrows());
        this.mediaObserver.observe(media);
      }

      // A page opened in a background tab (middle-click from the mega menu is
      // the common case) gets no animation frames at all, so an rAF-only
      // init would leave the arrows in their default hidden state until the
      // shopper happened to scroll. Do a synchronous pass now, refine it in a
      // frame, and re-run when the tab is actually shown.
      this.updateArrows();
      requestAnimationFrame(() => {
        this.positionFloatingArrows();
        this.updateArrows();
      });

      this.onVisibilityChange = () => {
        if (document.hidden) return;
        this.positionFloatingArrows();
        this.updateArrows();
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    restoreScroll() {
      var remembered = scrollMemory.get(this.memoryKey);
      if (!remembered) return;
      // Synchronously first (correct whenever layout is already resolved), then
      // again in a frame in case the track had no scrollWidth yet and the
      // assignment got clamped to 0.
      this.track.scrollLeft = remembered;
      requestAnimationFrame(() => {
        if (this.track.scrollLeft !== remembered) this.track.scrollLeft = remembered;
        this.updateArrows();
      });
    }

    disconnectedCallback() {
      this.mediaObserver?.disconnect();
      if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    onScroll() {
      scrollMemory.set(this.memoryKey, this.track.scrollLeft);
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => this.updateArrows());
    }

    onResize() {
      this.positionFloatingArrows();
      this.updateArrows();
    }

    metrics() {
      var maxScroll = this.track.scrollWidth - this.track.clientWidth;
      return {
        maxScroll: maxScroll,
        overflowing: maxScroll > 2,
        atStart: this.track.scrollLeft <= 2,
        atEnd: this.track.scrollLeft >= maxScroll - 2
      };
    }

    updateArrows() {
      if (!this.track) return;
      var m = this.metrics();

      if (this.navStyle === 'floating') {
        // Prev appears once you've scrolled, next until you hit the end.
        if (this.prevBtn) this.prevBtn.classList.toggle('is-visible', m.overflowing && !m.atStart);
        if (this.nextBtn) this.nextBtn.classList.toggle('is-visible', m.overflowing && !m.atEnd);
      } else {
        if (this.prevBtn) this.prevBtn.disabled = !m.overflowing || m.atStart;
        if (this.nextBtn) this.nextBtn.disabled = !m.overflowing || m.atEnd;
      }
    }

    positionFloatingArrows() {
      if (this.navStyle !== 'floating') return;
      var media = this.querySelector('.pj-cm-sub__media');
      if (!media) return;
      var h = media.getBoundingClientRect().height;
      if (!h) return;
      var top = Math.round(h / 2) + 'px';
      if (this.prevBtn) this.prevBtn.style.top = top;
      if (this.nextBtn) this.nextBtn.style.top = top;
    }

    scrollByOne(direction) {
      if (!this.track) return;
      var firstItem = this.track.querySelector('.pj-cm-sub__item');
      if (!firstItem) return;
      var gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 24;
      var step = firstItem.offsetWidth + gap;
      this.track.scrollBy({
        left: direction * step,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    }

    setupDrag() {
      // Desktop click-and-drag. Touch keeps native momentum scrolling.
      var self = this;
      var DRAG_THRESHOLD = 6;

      this.track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse') return;
        self._drag.active = true;
        self._drag.moved = false;
        self._drag.startX = e.clientX;
        self._drag.startScroll = self.track.scrollLeft;
      });

      this.track.addEventListener('pointermove', function (e) {
        if (!self._drag.active) return;
        var dx = e.clientX - self._drag.startX;
        if (!self._drag.moved && Math.abs(dx) > DRAG_THRESHOLD) {
          self._drag.moved = true;
          self.closest('.pj-cm-sub')?.classList.add('pj-cm-sub--grabbing');
          try { self.track.setPointerCapture(e.pointerId); } catch (err) {}
        }
        if (self._drag.moved) {
          self.track.scrollLeft = self._drag.startScroll - dx;
        }
      });

      function endDrag() {
        if (!self._drag.active) return;
        var moved = self._drag.moved;
        self._drag.active = false;
        self._drag.moved = false;
        var shell = self.closest('.pj-cm-sub');
        if (shell) shell.classList.remove('pj-cm-sub--grabbing');
        // Swallow the click that follows a real drag so it doesn't navigate.
        if (moved) {
          var swallow = function (ev) {
            ev.preventDefault();
            ev.stopPropagation();
            self.track.removeEventListener('click', swallow, true);
          };
          self.track.addEventListener('click', swallow, true);
        }
      }

      this.track.addEventListener('pointerup', endDrag);
      this.track.addEventListener('pointercancel', endDrag);
      this.track.addEventListener('pointerleave', endDrag);
    }
  }

  if (!customElements.get('pj-cm-subslider')) {
    customElements.define('pj-cm-subslider', PJCollectionSubSlider);
  }

  /* ====================================================================
     Bubble strip
     ==================================================================== */

  /**
   * A row of pills that ends flush with the container edge reads as finished,
   * so nobody tries to swipe it. These classes drive a fade on whichever edge
   * has more content behind it.
   */
  function updateScrollAffordance(strip) {
    if (!strip) return;
    var max = strip.scrollWidth - strip.clientWidth;
    var scrollable = max > 2;
    strip.classList.toggle('is-scrollable', scrollable);
    strip.classList.toggle('can-scroll-start', scrollable && strip.scrollLeft > 2);
    strip.classList.toggle('can-scroll-end', scrollable && strip.scrollLeft < max - 2);
  }

  function bindStrip(strip) {
    if (!strip || strip.pjBound) return;
    strip.pjBound = true;
    strip.addEventListener('scroll', function () { updateScrollAffordance(strip); }, { passive: true });
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { updateScrollAffordance(strip); }).observe(strip);
    }
    updateScrollAffordance(strip);
  }

  /**
   * In a single-row strip the active pill can easily start off-screen — the
   * shopper then has no idea where they are. Bring it into view without
   * animating, so it looks like it was always there.
   *
   * Aligned to the start of the strip rather than centred. Centring left the
   * first pill sliced down the middle, which reads as a rendering fault rather
   * than as "there is more this way"; aligning means a pill is either fully
   * shown or fully past the edge, and anything cut is cut on the right.
   */
  function revealActivePill(root) {
    var strip = (root || document).querySelector('.pj-cm-bub__inner');
    if (!strip) return;
    bindStrip(strip);

    var active = strip.querySelector('.pj-cm-bub__btn.is-active');
    if (!active) return;
    if (strip.scrollWidth <= strip.clientWidth + 2) return;

    var stripBox = strip.getBoundingClientRect();
    var pillBox = active.getBoundingClientRect();
    var padStart = parseFloat(getComputedStyle(strip).paddingInlineStart) || 0;

    // Already fully on screen — leave the shopper's view alone, which also
    // keeps the first pill whole in the common case.
    if (pillBox.left >= stripBox.left + padStart - 1 && pillBox.right <= stripBox.right + 1) {
      updateScrollAffordance(strip);
      return;
    }

    strip.scrollLeft = Math.max(0, strip.scrollLeft + (pillBox.left - stripBox.left) - padStart);
    updateScrollAffordance(strip);
  }

  /**
   * `.is-stuck` only once the bar has actually pinned, so the shadow reads as
   * a response to scrolling rather than a permanent border.
   */
  var stickObserver = null;

  function watchStick() {
    // The section is re-rendered on every facet change and sub-collection
    // switch, so this runs many times per visit; without disconnecting we
    // would accumulate an observer per render, each watching a detached node.
    if (stickObserver) stickObserver.disconnect();

    var sentinel = document.querySelector('.pj-cm-sub__sentinel');
    var bar = document.querySelector('.pj-cm-sub--sticky');
    if (!sentinel || !bar || !('IntersectionObserver' in window)) return;

    var top = parseInt(getComputedStyle(bar).top, 10) || 0;
    stickObserver = new IntersectionObserver(
      function (entries) {
        bar.classList.toggle('is-stuck', !entries[0].isIntersecting);
      },
      { rootMargin: -top + 'px 0px 0px 0px', threshold: 0 }
    );
    stickObserver.observe(sentinel);
  }

  /* ====================================================================
     Filter rail — collapsing the facet sidebar

     Desktop, sidebar layout only. Below 1000px the theme hides the sidebar
     and filtering happens in a drawer, which the theme already opens and
     closes from the other button in the rail — nothing to do here.

     The state lives in this module rather than on the element because the
     element does not survive: applying a filter re-renders the whole
     section, as does switching sub-collection.
     ==================================================================== */
  var filtersCollapsed = null;

  function syncFilterState() {
    var collection = document.querySelector('.collection--filters-sidebar');
    if (!collection) return;

    // First run adopts whatever the server rendered, so the setting for the
    // starting state is honoured without a flash of the wrong layout.
    if (filtersCollapsed === null) {
      filtersCollapsed = collection.classList.contains('pj-filters-collapsed');
    }

    collection.classList.toggle('pj-filters-collapsed', filtersCollapsed);

    var buttons = document.querySelectorAll('.pj-cm-filter--sidebar');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute('aria-expanded', filtersCollapsed ? 'false' : 'true');
    }
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) return;
    var button = event.target.closest('.pj-cm-filter--sidebar');
    if (!button) return;
    filtersCollapsed = !filtersCollapsed;
    syncFilterState();
  });

  /**
   * The theme replaces the section's children wholesale on every facet update.
   * That takes the collapsed class with it, so the sidebar would spring back
   * open the moment a shopper ticked a filter. Re-assert after each render.
   */
  var swapObserver = null;

  function watchSectionSwaps() {
    if (swapObserver) return;
    var root = document.querySelector('.pj-cm-sub');
    var section = root && root.closest('.shopify-section');
    if (!section || !('MutationObserver' in window)) return;

    swapObserver = new MutationObserver(function () {
      syncFilterState();
      watchStick();
    });
    // childList only: syncFilterState edits a class on a descendant, which is
    // an attribute change, so this cannot re-trigger itself.
    swapObserver.observe(section, { childList: true });
  }

  /* ====================================================================
     Smooth sub-collection switching

     Swaps the section over the section-rendering API — the same mechanism
     the theme's own facet-form uses — instead of navigating. Scroll position
     is simply never touched, which is what stops the page jumping.

     Everything here is enhancement: the pills and cards are real links, so
     no-JS, middle-click, and any failure all fall through to a normal
     navigation.
     ==================================================================== */

  var currentPath = location.pathname;
  var inFlight = null;
  var prefetched = new Map();
  var liveRegion = null;

  function announce(message) {
    if (!liveRegion) {
      liveRegion = document.createElement('p');
      liveRegion.className = 'pj-cm-bub__status';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion); // outside the section, so it survives the swap
    }
    liveRegion.textContent = message;
  }

  function shell() {
    return document.querySelector('.pj-cm-sub');
  }

  /**
   * A shopper who sorted by price and then picks the next sub-collection means
   * to keep that sort — dropping it silently re-sorts the page under them.
   * Facet filters are deliberately not carried: they are specific to the
   * collection they were chosen in and would strand people on empty results.
   */
  function carrySort(url) {
    var sort = new URLSearchParams(location.search).get('sort_by');
    if (sort) url.searchParams.set('sort_by', sort);
  }

  /**
   * The grid is not the only thing on the page that depends on which
   * collection we are looking at: the banner heading and the breadcrumb trail
   * are separate sections, and swapping only the grid left them describing the
   * collection the shopper had just left.
   *
   * `?sections=` takes a comma-separated list and returns one JSON object, so
   * all three arrive in a single request — 158KB against the 795KB a whole
   * document costs.
   */
  function companionIds() {
    var ids = [];
    var header = document.querySelector('.shopify-section--pj-category-header');
    if (header) ids.push(header.id.replace('shopify-section-', ''));
    var crumbs = document.querySelector('.breadcrumbs');
    var crumbSection = crumbs && crumbs.closest('.shopify-section');
    if (crumbSection) ids.push(crumbSection.id.replace('shopify-section-', ''));
    return ids;
  }

  function fetchUrl(href, view, sectionId) {
    var url = new URL(href, location.origin);
    if (view) url.searchParams.set('view', view);
    carrySort(url);
    url.searchParams.set('sections', [sectionId].concat(companionIds()).join(','));
    return url.toString();
  }

  function publicUrl(href, view) {
    var url = new URL(href, location.origin);
    if (view) url.searchParams.set('view', view);
    carrySort(url);
    return url.toString();
  }

  function prefetch(href, view, sectionId) {
    var key = fetchUrl(href, view, sectionId);
    if (prefetched.has(key)) return;
    if (prefetched.size > 8) prefetched.clear();
    prefetched.set(
      key,
      fetch(key).then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
                .catch(function () { prefetched.delete(key); return null; })
    );
  }

  /**
   * DOMParser documents have scripting disabled, so <noscript> is parsed as
   * real markup rather than inert text. Inserted into a live page its contents
   * take effect — a <noscript><style> becomes an active stylesheet.
   */
  function renderFragment(markup) {
    if (!markup) return null;
    var doc = new DOMParser().parseFromString(markup, 'text/html');
    var root = doc.querySelector('.shopify-section') || doc.body;
    if (!root) return null;
    var inert = root.querySelectorAll('noscript');
    for (var i = 0; i < inert.length; i++) inert[i].remove();
    return root;
  }

  async function swapTo(href, push, link) {
    var root = shell();
    if (!root) { location.href = href; return; }

    // replaceChildren destroys the element that was focused, which drops focus
    // to <body> — a keyboard user presses Enter on a pill and is silently
    // dumped at the top of the document. Remember where they were so the
    // equivalent control can be re-focused afterwards.
    var hadFocus = root.contains(document.activeElement);

    var section = root.closest('.shopify-section');
    var sectionId = root.dataset.sectionId;
    var view = root.dataset.view || '';
    if (!section || !sectionId) { location.href = href; return; }

    if (inFlight) inFlight.abort();
    inFlight = new AbortController();
    section.classList.add('pj-cm-sub-loading');
    announce('Loading…');

    var key = fetchUrl(href, view, sectionId);

    try {
      var html;
      if (prefetched.has(key)) {
        html = await prefetched.get(key);
        prefetched.delete(key);
      }
      if (!html) {
        var res = await fetch(key, {
          signal: inFlight.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        html = await res.text();
      }

      // Where the grid sits in the document right now. Re-syncing the banner
      // and breadcrumbs can change their height by a few pixels between
      // collections, which would nudge everything below them; measuring before
      // and after lets us cancel that out and keep the grid visually still.
      var anchorBefore = section.getBoundingClientRect().top + window.scrollY;

      var payload;
      try {
        payload = JSON.parse(html);
      } catch (parseError) {
        throw new Error('unexpected response shape');
      }

      var incoming = renderFragment(payload[sectionId]);
      if (!incoming) throw new Error('no section in response');

      // If the target collection sits on a template without this section, the
      // response won't contain a grid. Bail to a real navigation rather than
      // blanking the page.
      if (!incoming.querySelector('.collection__results, .empty-state')) {
        throw new Error('no grid in response');
      }

      section.replaceChildren(...document.importNode(incoming, true).childNodes);

      // Banner heading and breadcrumbs. Anything the response doesn't carry is
      // left exactly as it is rather than blanked.
      companionIds().forEach(function (id) {
        var target = document.getElementById('shopify-section-' + id);
        var fragment = renderFragment(payload[id]);
        if (target && fragment) {
          target.replaceChildren(...document.importNode(fragment, true).childNodes);
        }
      });

      if (push) history.pushState({ pjSubNav: true }, '', publicUrl(href, view));
      currentPath = new URL(href, location.origin).pathname;

      var drift = section.getBoundingClientRect().top + window.scrollY - anchorBefore;
      if (drift && window.scrollY > 0) window.scrollTo(0, window.scrollY + drift);

      revealActivePill(section);
      watchStick();
      syncFilterState();

      if (hadFocus) {
        var refocus = section.querySelector('.pj-cm-bub__btn.is-active') ||
                      section.querySelector('.pj-cm-bub__btn, .pj-cm-sub__card');
        // preventScroll matters: scrolling here would undo the whole point of
        // swapping in place.
        if (refocus) refocus.focus({ preventScroll: true });
      }

      // Each link carries the destination's real page title, so the tab shows
      // what a normal navigation would have shown. Deriving it from the markup
      // used to pick the first CARD's text in cards mode — which produced tab
      // titles like "Shop Now Kitchen Mixing Tools".
      var pageTitle = (link && link.dataset && link.dataset.pageTitle) || '';
      if (!pageTitle) {
        // Back/forward has no clicked link, but the pill that is now active
        // carries the same attribute.
        var activeNow = section.querySelector('.pj-cm-bub__btn.is-active');
        pageTitle = (activeNow && activeNow.dataset.pageTitle) || '';
      }
      if (!pageTitle) {
        // Cards mode has no active pill (no card links to the collection being
        // viewed), so the swapped-in section root carries its own title.
        var rootNow = section.querySelector('.pj-cm-sub');
        pageTitle = (rootNow && rootNow.dataset.pageTitle) || '';
      }

      var heading = document.querySelector('h1');
      var headingText = heading ? heading.textContent.replace(/\s+/g, ' ').trim() : '';
      if (!pageTitle) pageTitle = headingText;
      if (pageTitle) document.title = pageTitle;

      var name = headingText || pageTitle;
      announce((name || 'Collection') + ' loaded');

      document.dispatchEvent(new CustomEvent('pj:collection:sub-switched', {
        detail: { path: currentPath }
      }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Anything unexpected: let the browser do it properly.
      location.href = href;
    } finally {
      section.classList.remove('pj-cm-sub-loading');
      inFlight = null;
    }
  }

  function subLink(target) {
    return target.closest ? target.closest('.pj-cm-bub__btn, .pj-cm-sub__card') : null;
  }

  function eligible(link) {
    if (!link) return null;
    var root = link.closest('.pj-cm-sub');
    if (!root || root.dataset.smoothSwitch !== 'true') return null;
    var href = link.getAttribute('href');
    if (!href || href.charAt(0) !== '/') return null;
    if (new URL(href, location.origin).pathname === location.pathname) return null;
    return { root: root, href: href, link: link };
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var hit = eligible(subLink(event.target));
    if (!hit) return;
    event.preventDefault();
    swapTo(hit.href, true, hit.link);
  });

  // Warm the next page on hover. Hover-capable pointers only — on touch this
  // would fire on every incidental tap.
  if (window.matchMedia('(hover: hover)').matches) {
    document.addEventListener('pointerenter', function (event) {
      var hit = eligible(subLink(event.target));
      if (!hit) return;
      prefetch(hit.href, hit.root.dataset.view || '', hit.root.dataset.sectionId);
    }, true);
  }

  window.addEventListener('popstate', function () {
    if (location.pathname === currentPath) return;
    if (!shell()) { location.reload(); return; }
    swapTo(location.pathname + location.search, false);
  });

  /**
   * documentElement.clientWidth is the viewport minus any classic scrollbar —
   * exactly what `100vw` gets wrong. Publishing it lets the sticky bar bleed
   * full width without introducing a horizontal scroll.
   */
  function measureViewport() {
    var width = document.documentElement.clientWidth;
    if (!width) return;
    document.documentElement.style.setProperty('--pj-viewport-width', width + 'px');
    document.documentElement.classList.add('pj-fullbleed-ok');
  }

  function init() {
    measureViewport();
    revealActivePill(document);
    watchStick();
    syncFilterState();
    watchSectionSwaps();

    // Pill widths change when the web font swaps in, which can flip the strip
    // between overflowing and not. The ResizeObserver catches it, but only on
    // the next rendering step — re-check directly so the fade never shows on a
    // row that has nothing more to reveal.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        updateScrollAffordance(document.querySelector('.pj-cm-bub__inner'));
      });
    }
  }

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      measureViewport();
      updateScrollAffordance(document.querySelector('.pj-cm-bub__inner'));
    }, 120);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
